import React, { useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';

export interface MetricCardTrend {
    value: string;
    isPositive: boolean;
    label: string;
    /** When false, show factual trend line without up/down arrows or success/danger coloring. */
    emphasizeDirection?: boolean;
}

interface MetricCardProps {
    label: string;
    value: string | number;
    icon: IconDefinition;
    trend?: MetricCardTrend;
    color?: string;
    /** Short operational context: what question this metric answers. */
    helperText?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
    label,
    value,
    icon,
    trend,
    color = 'text-electric-blue',
    helperText,
}) => {
    const helperId = useId();
    const describeBy = helperText ? helperId : undefined;
    const emphasizeDirection = trend?.emphasizeDirection !== false;

    return (
        <div
            className="relative overflow-hidden rounded-2xl border p-5 shadow-xl backdrop-blur-sm group transition-all"
            style={{ background: 'var(--color-bg-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-card)' }}
            aria-describedby={describeBy}
        >
            <div className="flex justify-between items-start mb-2">
                <span className="text-theme-secondary text-sm font-medium">{label}</span>
                <div className={`w-10 h-10 rounded-xl bg-theme-secondary flex items-center justify-center ${color} shadow-inner border border-theme`}>
                    <FontAwesomeIcon icon={icon} className="text-lg" />
                </div>
            </div>

            {helperText ? (
                <p id={helperId} className="text-[11px] leading-snug text-theme-muted mb-3">
                    {helperText}
                </p>
            ) : null}

            <div className="text-4xl font-bold text-theme tracking-tight mb-4">
                {value}
            </div>

            {trend && (
                <div
                    className={`flex items-center text-xs font-bold ${
                        emphasizeDirection
                            ? trend.isPositive
                                ? 'text-status-offer'
                                : 'text-status-rejected'
                            : 'text-theme-muted'
                    }`}
                >
                    {emphasizeDirection ? (
                        <FontAwesomeIcon icon={trend.isPositive ? faArrowUp : faArrowDown} className="mr-1" />
                    ) : null}
                    <span>{trend.value}</span>
                    <span className="text-theme-muted font-medium ml-1">{trend.label}</span>
                </div>
            )}
        </div>
    );
};

export default MetricCard;
